import React from 'react';
import { SectionHeading } from './SectionHeading';

/**
 * BLOCK: prose
 * ACF layout name: `block_prose`
 *
 * Fields:
 *  - eyebrow     (text)
 *  - heading     (text)
 *  - variant     (select: plain | bordered | panel)
 *  - paragraphs  (repeater: text, style[body|lead|accent|statement])
 *
 * The long-form editorial sections on the service pages ("What is Sopotje?",
 * "My role", "How does the session work?", …) were previously hand-written JSX
 * with per-paragraph Tailwind classes. They are now data-driven: one repeater
 * row per paragraph, with a style token instead of raw classes — which is what
 * makes them portable to ACF.
 */

export type ProseStyle = 'body' | 'lead' | 'accent' | 'statement';
export interface ProseParagraph {
  text: string;
  style?: ProseStyle;
}
export interface ProseSectionContent {
  id: string;
  eyebrow?: string;
  heading: string;
  variant?: 'plain' | 'bordered' | 'panel';
  paragraphs: readonly ProseParagraph[];
}
const PARAGRAPH_CLASS: Record<ProseStyle, string> = {
  body: '',
  lead: 'font-serif text-2xl italic text-charcoal',
  accent: 'font-serif text-2xl italic text-sage-700',
  statement: 'font-serif text-2xl text-charcoal'
};
const WRAPPER_CLASS: Record<NonNullable<ProseSectionContent['variant']>, string> = {
  plain: '',
  bordered: 'border-l-2 border-sage pl-6 sm:pl-9',
  panel: 'bg-sage-50 px-7 py-10 sm:px-10 sm:py-12'
};
export function ProseSection({
  id,
  eyebrow,
  heading,
  variant = 'plain',
  paragraphs
}: ProseSectionContent) {
  return <section aria-labelledby={id} className={WRAPPER_CLASS[variant]} data-id="element-153" data-mp="2a3bkzmbrcppc">
      <SectionHeading eyebrow={eyebrow} heading={heading} id={id} data-id="element-154" data-mp="261q47pw3rr9v" />
      <div className="mt-7 max-w-2xl space-y-5 text-base font-light leading-relaxed text-charcoal-50" data-id="element-155" data-mp="213etze1wber4">
        {paragraphs.map(paragraph => <p key={paragraph.text} className={PARAGRAPH_CLASS[paragraph.style ?? 'body']} data-id="element-156" data-mp="4a3ia2r2imk9">
            {paragraph.text}
          </p>)}
      </div>
    </section>;
}